import { checkTypeExhausted, nonUndefined } from "Utility";
import { mapTrackPicks } from "../Track/FsTrackPick";
import { mapWorldTourSet } from "./WorldTour/FsWorldTourSet";
import { mapForwardCupModeSet } from "./CupMode/Forward/FsForwardCupModeSet";
import { mapReverseCupModeSet } from "./CupMode/Reverse/FsReverseCupModeSet";
import { mapRoundsSet } from "./Rounds/FsRoundsSet";
import { mapTimeAttackSet } from "./TimeAttack/FsTimeAttackSet";
import { mapMatchmakingSet } from "./Matchmaking/FsMatchmakingSet";
import { MatchTeams } from "model/Match/MatchTeams";
import { MatchSet, MatchSetBase } from "model/Match/Set/MatchSet";
import { FsMatchSet, FsMatchSetBase } from "db/model/match/Set/FsMatchSet";
import { PlayCondition } from "model/Match/PlayCondition";
import { Scores } from "model/Scores";
import { mapForwardCupModeLongSet } from "./CupModeLong/Forward/FsForwardCupModeLongSet";
import { Positions } from "model/Positions";
import { CupModeLongTrack } from "model/Match/Set/CupMode/Track/CupModeLongTrack";
import { WorldTourTrack } from "model/Match/Set/WorldTour/Track/WorldTourTrack";
import { mapSetPointRules } from "db/mapping/match/Set/FsSetPointRules";
import { getResults } from "model/Results";
import { mapKnockoutSet } from "./Knockout/FsKnockoutSet";
import { mapRoundsKnockoutSet } from "./RoundsKnockout/FsRoundsKnockoutSet";

const YEKOUT_MATCH_ID = "1749924000000x001";

export const mapMatchSetBase = (
  fsModel: FsMatchSetBase,
  isComplete: boolean,
  finalPositions: Positions | undefined,
  scores: Scores | undefined
): MatchSetBase => {
  return {
    finalPositions,
    isComplete,
    isTiebreaker: fsModel.isTiebreaker ?? false,
    picks: mapTrackPicks(nonUndefined(fsModel.picks ?? [])),
    points: !!fsModel.points ? mapSetPointRules(fsModel.points) : undefined,
    scores,
  };
};

export const mapMatchSets = (
  fsModels: FsMatchSet[],
  teams: MatchTeams,
  tournamentId: string,
  matchId: string
): MatchSet[] => {
  return nonUndefined(
    fsModels.map((s) => mapMatchSet(s, teams, tournamentId, matchId))
  );
};

const mapMatchSet = (
  fsModel: FsMatchSet,
  teams: MatchTeams,
  tournamentId: string,
  matchId: string
): MatchSet | undefined => {
  switch (fsModel.type) {
    case "ForwardCupMode":
      return mapForwardCupModeSet(fsModel, teams);
    case "ForwardCupModeLong":
      return mapForwardCupModeLongSet(fsModel, teams);
    case "Knockout":
      return mapKnockoutSet(fsModel, teams, matchId === YEKOUT_MATCH_ID);
    case "Matchmaking":
      return mapMatchmakingSet(fsModel, teams, tournamentId === "Smsx1");
    case "ReverseCupMode":
      return mapReverseCupModeSet(fsModel, teams);
    case "Rounds":
      return mapRoundsSet(fsModel, teams);
    case "RoundsKnockout":
      return mapRoundsKnockoutSet(
        fsModel,
        teams,
        tournamentId === "MajiMondayx4" ? "Best" : "LastPos"
      );
    case "TimeAttack":
      return mapTimeAttackSet(fsModel, teams);
    case "WorldTour":
      return mapWorldTourSet(fsModel, teams);
    default:
      return checkTypeExhausted(fsModel);
  }
};

export const calculateIsCompletePlayCondition = (
  scores: Scores,
  numTracks: number,
  numCompleteTracks: number,
  playCondition: PlayCondition
) => {
  const totalPoints = numTracks;
  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const remainingPoints = totalPoints - totalScore;

  switch (playCondition) {
    case "BestOf": {
      if (numCompleteTracks === numTracks) {
        return true;
      }

      const scoreValues = Object.values(scores);
      if (scoreValues.length <= 1) {
        return false;
      }

      const bestScore = Math.max(...scoreValues, 0);
      const tiedBest = scoreValues.filter((s) => s === bestScore).length > 1;
      if (tiedBest) {
        return false;
      }

      const otherScoreValues = scoreValues.filter((s) => s < bestScore);
      const nextBestScore = Math.max(...otherScoreValues, 0);
      return bestScore > nextBestScore + remainingPoints;
    }
    case "FirstTo": {
      return Math.max(...Object.values(scores), 0) >= numTracks;
    }
    case "PlayAll":
      return numCompleteTracks === numTracks || totalPoints === totalScore;
    default:
      checkTypeExhausted(playCondition);
      return false;
  }
};

// Assumes one winner per track
export const calculateTrackPointBasedSetScores = (
  tracks: (CupModeLongTrack | WorldTourTrack)[],
  scoreOverrides: Scores | undefined,
  rosterIds: string[]
) => {
  const scores: Scores = {};
  const trackResults = nonUndefined(
    tracks.map((t) => getResults(t.finalPositions, 1))
  );
  for (var i = 0; i < rosterIds.length; i++) {
    const rosterId = rosterIds[i];
    const scoreOverride = !!scoreOverrides
      ? scoreOverrides[rosterId]
      : undefined;
    if (scoreOverride !== undefined) {
      scores[rosterId] = scoreOverride;
    } else {
      scores[rosterId] = trackResults.filter(
        (results) => results[rosterId] === "W"
      ).length;
    }
  }

  return scores;
};
